/**
 *
 * @module subscriptionManager
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import Token from './../token/entry';
import Subscription from './subscription';

const AccessTokenProvider = Token.AccessTokenProvider;
const SubscriptionManagerConfiguration = Services.Configuration.SubscriptionManagerConfiguration;
const SubscriptionClient = Services.Subscription.SubscriptionClient;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict
} = Check.assert;

/**
 *
 * @class SubscriptionManager
 *
 */
export default class SubscriptionManager {

    /**
     *
     * @param {SDK.Services.Configuration.SubscriptionManagerConfiguration} subscriptionManagerConfiguration
     * @param {SDK.Services.Subscription.SubscriptionClient} subscriptionClient
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {SDK.Token.AccessTokenProvider} accessTokenProvider
     *
     */
    constructor(subscriptionManagerConfiguration, subscriptionClient, logger = Logging, accessTokenProvider) {

        const errorMessage = {
            ctor: 'SubscriptionManager(subscriptionManagerConfiguration, subscriptionClient, logger, accessTokenProvider)', // eslint-disable-line
            get subscriptionManagerConfiguration() {
                return `${this.ctor} needs SubscriptionManagerConfiguration instance`;
            },
            get subscriptionClient() {
                return `${this.ctor} needs SDK.Services.Subscription.SubscriptionClient instance`;
            },
            get accessTokenProvider() {
                return `${this.ctor} needs SDK.Token.AccessTokenProvider instance`;
            }
        };

        instanceStrict(subscriptionManagerConfiguration, SubscriptionManagerConfiguration, errorMessage.subscriptionManagerConfiguration, InvalidArgumentException); // eslint-disable-line
        instanceStrict(subscriptionClient, SubscriptionClient, errorMessage.subscriptionClient, InvalidArgumentException); // eslint-disable-line
        instanceStrict(accessTokenProvider, AccessTokenProvider, errorMessage.accessTokenProvider, InvalidArgumentException); // eslint-disable-line

        /**
         *
         * @access private
         * @type {SDK.Services.Configuration.SubscriptionManagerConfiguration}
         *
         */
        this.config = subscriptionManagerConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Services.Subscription.SubscriptionClient}
         *
         */
        this.client = subscriptionClient;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {SDK.Token.AccessTokenProvider}
         *
         */
        this.accessTokenProvider = accessTokenProvider;

        this.logger.log(SubscriptionManager.name, 'Created.');

    }

    /**
     *
     * @access private
     * @desc Returns a list of subscriptions for the current user.
     * @returns {Promise<Array<SDK.Commerce.Subscription>>}
     *
     */
    getSubscriptions() {

        return this.client.getSubscriptions(this.accessToken).then((subscriptionData) => {

            const subscriptions = [];

            if (Check.nonEmptyArray(subscriptionData)) {

                subscriptionData.forEach((subscription) => {
                    subscriptions.push(new Subscription(subscription));
                });

            }

            return Promise.resolve(subscriptions);

        });

    }

    /**
     *
     * @access private
     * @desc Links (copies) all subscriptions from the user's device to the Account.
     * @returns {Promise<Void>}
     *
     */
    linkSubscriptionsFromDevice() {

        return this.client.linkSubscriptionsFromDevice(this.accessToken);

    }

    /* #region private: */

    /**
     *
     * @access private
     * @desc Grabs a fresh AccessToken from the AccessTokenProvider instance
     * @returns {SDK.Token.AccessToken}
     *
     */
    get accessToken() {

        return this.accessTokenProvider.getAccessToken();

    }

}



// WEBPACK FOOTER //
// ./src/subscription/subscriptionManager.js